import { useState, useEffect, forwardRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

import Button from "@mui/material/Button";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Grid from "@mui/material/Grid";

import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";

import { useAuth } from "../../../auth";

//Snackbar
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

//Loader spinner
import CircularProgress from "@mui/material/CircularProgress";

//Select Client Modal
import SelectClientModal from "./SelectClientModal";

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

dayjs.extend(localizedFormat);

const AddAppointmentModal = ({ clientDetails }) => {
  const [open, setOpen] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [slotsList, setSlotsList] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientData, setClientData] = useState({
    clientID: 0,
    slotID: 0,
    appointmentDate: dayjs().format("DD/MM/YYYY"),
    appointmentDuration: 30,
    appointmentStartTime: "00:00 AM",
  });

  //Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSelectClientModal, setOpenSelectClientModal] = useState(false);
  const [isAppointmentAdded, setIsAppointmentAdded] = useState(null);

  const { currentUser } = useAuth();

  const headers = {
    "X-API-Key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
    "Content-Type": "application/json",
  };

  const userID = JSON.parse(localStorage.getItem("profile")).UserId;

  const postSlotsData = async () => {
    const payload = {
      expert_id: userID,
      date: dayjs(clientData.appointmentDate, "DD/MM/YYYY").format(
        "YYYY-MM-DD"
      ),
      duration: 60,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      const queryParams = new URLSearchParams({
        expert_id: payload.expert_id,
        date: payload.date,
        duration: payload.duration,
        timezone: payload.timezone,
      });

      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/slots?${queryParams}`,
        {
          method: "GET",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();

      // Transform the slots data
      const selectedDate = payload.date;
      const slotsForDate = data?.data?.slots[selectedDate] || [];

      const formattedSlots = slotsForDate.map((slot, index) => ({
        id: index + 1,
        slot: `${dayjs(slot.time).format("hh:mm A")} - ${dayjs(slot.time)
          .add(clientData.appointmentDuration, "minute")
          .format("hh:mm A")}`,
        time: slot.time,
      }));

      setSlotsList(formattedSlots);
    } catch (error) {
      console.error("Error:", error);
      setSlotsList([]);
    }
  };

  useEffect(() => {
    if (clientDetails) {
      setSelectedClient(clientDetails);
    }
  }, [clientDetails]);
  //Slots List (Reschedule Dialog)
  useEffect(() => {
    postSlotsData();

    if (clientDetails) {
      setClientData((prevState) => ({
        ...prevState,
        clientID: clientDetails.id,
      }));
    }
  }, [clientData.appointmentDate]);

  //Fetch Clients
  // useEffect(() => {
  //   const payload = {
  //     expert_id: currentUser?.UserId,
  //   };

  //   const postClientsData = async () => {
  //     try {
  //       const response = await fetch(
  //         `https://odoo.unitedwecare.ca/new-uwc/expert/dashboard/clients`,
  //         {
  //           method: "POST",
  //           headers,
  //           body: JSON.stringify(payload),
  //         }
  //       );
  //       const postResponse = await response.json();

  //       const { clients } = await postResponse.data;

  //       setClientList(clients);
  //     } catch (error) {
  //       console.error("Error:", error);
  //     }
  //   };

  //   postClientsData();
  // }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setClientData({
      clientID: 0,
      slotID: 0,
      appointmentDate: dayjs().format("DD/MM/YYYY"),
      appointmentDuration: 30,
      appointmentStartTime: "00:00 AM",
    });
  };

  const handleSlotChange = (slot) => {
    setClientData((prevClientData) => ({
      ...prevClientData,
      slotID: slot.id,
      appointmentStartTime: slot.slot.split(" - ")[0],
    }));
  };

  const onToDateChange = (date) => {
    const formattedDate = dayjs(date).format("DD/MM/YYYY");
    setClientData((prevClientData) => ({
      ...prevClientData,
      appointmentDate: formattedDate,
    }));
  };

  const handleBookAppointment = async () => {
    setIsAppointmentAdded(false);
    const sessionBearerToken = sessionStorage.getItem("token");

    // Format the appointment date and time
    const appointmentDateTime = dayjs(
      `${clientData?.appointmentDate} ${clientData?.appointmentStartTime}`,
      "DD/MM/YYYY hh:mm A"
    ).format("YYYY-MM-DD HH:mm:ss");

    const payload = {
      expert_id: +currentUser?.UserId,
      client_id: +selectedClient?.ID,
      appointment_time: appointmentDateTime,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Gets local timezone
      token: sessionBearerToken,
      duration: +clientData?.appointmentDuration,
    };

    const addAppointmentHeaders = {
      accept: "application/json, text/plain, */*",
      "content-type": "application/json",
      deviceid: "123",
      devicetype: "web",
      lat: "1",
      long: "1",
      authorization: `Bearer ${sessionBearerToken}`,
      "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
      "x-email-id": currentUser?.Emailid,
    };

    try {
      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/book-now`,
        {
          method: "POST",
          headers: addAppointmentHeaders,
          body: JSON.stringify(payload),
        }
      );
      const postResponse = await response.json();

      if (postResponse?.code === 200) {
        setOpenSnackbar(true);
        setSnackbarMessage(
          postResponse?.data?.message || "Appointment booked successfully!"
        );
        handleClose();
        setIsAppointmentAdded(true);
        setSelectedClient(null);
      } else {
        setOpenSnackbar(true);
        setSnackbarMessage(
          postResponse?.message || "Failed to book appointment"
        );
        setIsAppointmentAdded(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setOpenSnackbar(true);
      setSnackbarMessage("An error occurred while booking the appointment");
      setIsAppointmentAdded(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackbar(false);
  };

  // const filteredSlots = slotsList?.filter((item) => {
  //   if (!checked) {
  //     // Render morning slots (AM)
  //     return item.slot.split(" - ")[0].includes("AM");
  //   } else {
  //     // Render evening slots (PM)
  //     return item.slot.split(" - ")[0].includes("PM");
  //   }
  // });

  // const sortedSlots = filteredSlots?.sort((a, b) => {
  //   const sortKeyA = a.slot.split(" - ")[0];
  //   const sortKeyB = b.slot.split(" - ")[0];
  //   return sortKeyA.localeCompare(sortKeyB);
  // });

  const handleSlotTimingChange = (appointmentDuration) => {
    setClientData((prevData) => ({
      ...prevData,
      appointmentDuration,
    }));
  };
  return (
    <>
      <Button
        onClick={handleClickOpen}
        sx={{ flex: "1", minWidth: "0" }}
        variant="contained"
        className="AddAppointment PrimaryCTA"
      >
        <AddCircleIcon />
        Add Appointment
      </Button>

      <Dialog
        className="AddApointment--Dialog"
        open={open}
        onClose={handleClose}
        sx={{
          maxWidth: "610px",
          width: "100%",
          minWidth: "580px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {isAppointmentAdded !== null && !isAppointmentAdded && (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "rgb(183 216 237 / 58%)",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: "10",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </div>
        )}
        <DialogTitle className="pb-2">Add Appointment</DialogTitle>
        <DialogContent className="Dialog--Content pt-2">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Pick a date"
              format="DD/MM/YYYY"
              value={dayjs(clientData?.appointmentDate, "DD/MM/YYYY")}
              onChange={onToDateChange}
              disablePast={true}
              sx={{ width: "100%" }}
            />
          </LocalizationProvider>

          <Grid
            container
            gap={2}
            sx={{ width: "100%", margin: "0" }}
            className="Timings"
          >
            {slotsList?.length > 0 ? (
              slotsList?.map((item) => (
                <Grid
                  item
                  xs={2}
                  key={item.slot}
                  className={`AvailableSlot ${
                    item.id == clientData?.slotID ? "SelectedSlot" : ""
                  }`}
                  onClick={() => handleSlotChange(item)}
                >
                  <p>{item.slot.split(" - ")[0]}</p>
                </Grid>
              ))
            ) : (
              <p className="mx-auto" style={{ fontWeight: "500" }}>
                No slots available for selected dates
              </p>
            )}
          </Grid>

          <div className="SessionDuration--Container">
            <h6>Session Duration</h6>

            <Grid
              container
              gap={2}
              sx={{ width: "100%", margin: "0 0 20px 0" }}
              className="SessionTimings"
            >
              {currentUser?.Country == "India" ? (
                <>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      cursor: "pointer",
                    }}
                    className={`${
                      clientData.appointmentDuration == "60"
                        ? "SelectedSTiming"
                        : ""
                    }`}
                    onClick={() => handleSlotTimingChange(60)}
                  >
                    <p>60 minutes</p>
                  </Grid>
                </>
              ) : (
                <Grid
                  item
                  xs={3}
                  className={`${
                    clientData.appointmentDuration == "50"
                      ? "SelectedSTiming"
                      : ""
                  }`}
                  onClick={() => handleSlotTimingChange(50)}
                >
                  <p>50 minutes</p>
                </Grid>
              )}
            </Grid>
          </div>

          <FormControl fullWidth className="mt-4 ClientSelect--Form">
            <InputLabel id="demo-simple-select-label">Select Client</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedClient?.ID || ""}
              open={false}
              onClick={() => setOpenSelectClientModal(true)}
              renderValue={() => {
                return selectedClient ? (
                  <div className="d-flex gap-2 ClientSelect--MenuItem">
                    <img
                      src={selectedClient.ProfileImage}
                      width={45}
                      height={45}
                      style={{ borderRadius: "50%", objectFit: "cover" }}
                      alt={selectedClient.Name}
                    />
                    <div>
                      <h6>{selectedClient.Name}</h6>
                      <p>{selectedClient.Country}</p>
                    </div>
                  </div>
                ) : (
                  <span>Select a client</span>
                );
              }}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            className="PrimaryCTA"
            sx={{
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: "20px",
            }}
            onClick={handleBookAppointment}
            variant="contained"
            disableElevation
            disabled={clientData.slotID !== 0 && selectedClient ? false : true}
          >
            Confirm and Book Appointment
          </Button>
        </DialogActions>
      </Dialog>
      <SelectClientModal
        open={openSelectClientModal}
        handleClose={() => setOpenSelectClientModal(false)}
        setSelectedClient={(value) => setSelectedClient(value)}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddAppointmentModal;
